import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiMove, FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../config/config';

const CoursePreferences = () => {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPreference, setEditingPreference] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    color: '#6366f1',
    position: 0,
    status: 1
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/course-preferences`);
      if (response.data.success) {
        setPreferences(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Error loading preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.slug) {
      toast.error('Name and slug are required');
      return;
    }

    try {
      const url = editingPreference
        ? `${API_BASE_URL}/institute/master/preference/${editingPreference.id}/update`
        : `${API_BASE_URL}/institute/master/preference/create`;

      const response = await axios.post(url, formData);

      if (response.data.success) {
        toast.success(editingPreference ? 'Preference updated!' : 'Preference created!');
        setIsModalOpen(false);
        resetForm();
        fetchPreferences();
      } else {
        toast.error(response.data.message || 'Error saving preference');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error saving preference');
    }
  };

  const handleEdit = (preference) => {
    setEditingPreference(preference);
    setFormData({
      name: preference.name,
      slug: preference.slug,
      description: preference.description || '',
      icon: preference.icon || '',
      color: preference.color || '#6366f1',
      position: preference.position || 0,
      status: preference.status || 1
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this preference? All associated course types will be unlinked.')) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/institute/master/preference/${id}`);
      if (response.data.success) {
        toast.success('Preference deleted successfully');
        fetchPreferences();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error deleting preference');
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/institute/master/preference/${id}/toggle-status`);
      if (response.data.success) {
        toast.success(currentStatus === 1 ? 'Preference disabled' : 'Preference enabled');
        fetchPreferences();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error updating status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '',
      color: '#6366f1',
      position: 0,
      status: 1
    });
    setEditingPreference(null);
  };

  const handleOpenModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      name: name,
      slug: editingPreference ? formData.slug : generateSlug(name)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Course Preferences</h1>
          <p className="text-gray-600 mt-1">Manage top-level course categories for student filtering</p>
        </div>
        <button
          onClick={handleOpenModal}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
        >
          <FiPlus /> Add Preference
        </button>
      </div>

      {/* Preferences Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {preferences.map((pref) => (
          <div
            key={pref.id}
            className={`border-2 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-all ${
              pref.status === 0 ? 'opacity-50' : ''
            }`}
            style={{ borderTopColor: pref.color, borderTopWidth: '4px' }}
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <span className="text-3xl">{pref.icon || '📚'}</span>
                <div className="flex items-center gap-2">
                  {pref.status === 0 && (
                    <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Inactive</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleEdit(pref)}
                  className="text-blue-600 hover:bg-blue-50 p-1 rounded"
                  title="Edit"
                >
                  <FiEdit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => toggleStatus(pref.id, pref.status)}
                  className={`${
                    pref.status === 1 ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'
                  } p-1 rounded`}
                  title={pref.status === 1 ? 'Disable' : 'Enable'}
                >
                  {pref.status === 1 ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => handleDelete(pref.id)}
                  className="text-red-600 hover:bg-red-50 p-1 rounded"
                  title="Delete"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <h3 className="font-bold text-gray-900 mb-2 text-lg">{pref.name}</h3>
            <p className="text-gray-600 text-sm mb-3 line-clamp-2">{pref.description}</p>

            {/* Stats */}
            <div className="flex justify-between text-sm pt-3 border-t">
              <div className="text-center">
                <p className="text-gray-500">Types</p>
                <p className="font-bold text-gray-900">{pref.course_types_count || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-500">Courses</p>
                <p className="font-bold text-gray-900">{pref.courses_count || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-500">Position</p>
                <p className="font-bold text-gray-900">{pref.position}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {preferences.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📚</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Preferences Yet</h3>
          <p className="text-gray-500 mb-4">Create your first course preference category</p>
          <button
            onClick={handleOpenModal}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Preference
          </button>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingPreference ? 'Edit Preference' : 'Add New Preference'}
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preference Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Academic Courses"
                  required
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slug <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., academic-courses"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">URL-friendly identifier (auto-generated from name)</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Brief description of this preference category"
                />
              </div>

              {/* Icon and Color Row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Icon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Icon (Emoji)</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-2xl text-center"
                    placeholder="📚"
                    maxLength="2"
                  />
                  <p className="text-xs text-gray-500 mt-1">Single emoji</p>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="#6366f1"
                    />
                  </div>
                </div>
              </div>

              {/* Position and Status Row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Position */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Position</label>
                  <input
                    type="number"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              {/* Preview */}
              {(formData.icon || formData.name) && (
                <div className="border-2 rounded-lg p-4 bg-gray-50" style={{ borderColor: formData.color }}>
                  <p className="text-xs text-gray-500 mb-2">Preview:</p>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{formData.icon || '📚'}</span>
                    <div>
                      <h4 className="font-bold text-gray-900">{formData.name || 'Preference Name'}</h4>
                      <p className="text-sm text-gray-600">{formData.description || 'Description will appear here'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <FiSave />
                  {editingPreference ? 'Update' : 'Create'} Preference
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursePreferences;

